import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import axios from "axios"
import { API_URL } from "@/shared"
import { showNotification } from "@mantine/notifications"
import { CreateViaticoDto, Viatico, ViaticoSection, ViaticoSectionAccounting } from "../domain/viatico.interface"

export const VIATICO_KEYS = {
  GET_ALL: "VIATICO_GET_ALL",
  GET_BY_ID: "VIATICO_GET_BY_ID"
}

export function useGetViaticos() {
  return useQuery({
    queryKey: [VIATICO_KEYS.GET_ALL],
    queryFn: async () => {
      const response = await axios.get(`${API_URL}/viatico`)
      return response.data.data as Viatico[]
    }
  })
}

export function useGetViatico(id: string) {
  return useQuery({
    queryKey: [VIATICO_KEYS.GET_BY_ID, id],
    queryFn: async () => {
      const response = await axios.get(`${API_URL}/viatico/${id}`)
      return response.data.data || (response.data as Viatico)
    },
    enabled: !!id
  })
}

export function useCreateViatico() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (data: CreateViaticoDto) => {
      const response = await axios.post(`${API_URL}/viatico`, data)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [VIATICO_KEYS.GET_ALL] })
      showNotification({ title: "Éxito", message: "Rendición de viático creada correctamente", color: "green" })
    }
  })
}

export function useUpdateViatico() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<Viatico> }) => {
      const response = await axios.patch(`${API_URL}/viatico/${id}`, data)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [VIATICO_KEYS.GET_ALL] })
      queryClient.invalidateQueries({ queryKey: [VIATICO_KEYS.GET_BY_ID] })
    }
  })
}

export function useApproveViatico() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, approvedBy }: { id: string; approvedBy: string }) => {
      const response = await axios.patch(`${API_URL}/viatico/${id}/approve`, { approvedBy })
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [VIATICO_KEYS.GET_ALL] })
      showNotification({ title: "Éxito", message: "Viático aprobado", color: "green" })
    }
  })
}

export function usePayViatico() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, paidBy }: { id: string; paidBy: string }) => {
      const response = await axios.patch(`${API_URL}/viatico/${id}/pay`, { paidBy })
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [VIATICO_KEYS.GET_ALL] })
      showNotification({ title: "Éxito", message: "Viático marcado como pagado", color: "green" })
    }
  })
}

export function useAnalyzeViatico() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, imageUrl, section }: { id: string; imageUrl: string; section: ViaticoSection }) => {
      const response = await axios.post(`${API_URL}/viatico/${id}/analyze`, { imageUrl, section })
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [VIATICO_KEYS.GET_ALL] })
      queryClient.invalidateQueries({ queryKey: [VIATICO_KEYS.GET_BY_ID] })
      showNotification({ title: "IA", message: "Imagen analizada y datos cargados", color: "violet" })
    }
  })
}

export function useAnalyzeViaticoYape() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, imageUrl, section }: { id: string; imageUrl: string; section: ViaticoSection }) => {
      const response = await axios.post(`${API_URL}/viatico/${id}/analyze-yape`, { imageUrl, section })
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [VIATICO_KEYS.GET_ALL] })
      queryClient.invalidateQueries({ queryKey: [VIATICO_KEYS.GET_BY_ID] })
      showNotification({ title: "IA", message: "Comprobante de Yape/Plin analizado y datos cargados", color: "violet" })
    }
  })
}

export function useSubmitViaticoAccounting() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, accounting }: { id: string; accounting: Partial<Record<ViaticoSection, ViaticoSectionAccounting>> }) => {
      const response = await axios.patch(`${API_URL}/viatico/${id}/accounting`, { accounting })
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [VIATICO_KEYS.GET_ALL] })
      queryClient.invalidateQueries({ queryKey: [VIATICO_KEYS.GET_BY_ID] })
      showNotification({ title: "Éxito", message: "Rendición contabilizada correctamente", color: "green" })
    }
  })
}
